import { distance } from 'fastest-levenshtein';

import { removeDiacritics } from '@e-socna/utils';

import { toleratedTyposOf } from './search-tolerance.util';
import type { SearchTolerance } from './search-tolerance.util';

const WORD_SEPARATOR = /[\s\-'’.,/()]+/u;

const normalized = (text: string) => removeDiacritics(text).toLowerCase();

// The searched word with two adjacent letters swapped, each way: an inversion is one typo, while
// the Levenshtein distance counts it as two.
const swappedVariantsOf = (word: string): readonly string[] =>
  Array.from(
    { length: Math.max(word.length - 1, 0) },
    (_, index) =>
      `${word.slice(0, index)}${word.charAt(index + 1)}${word.charAt(index)}${word.slice(index + 2)}`,
  );

// The edit distance of the searched word to a word of the text, or to its start (the user typing
// it).
const distanceTo = (textWord: string) => (word: string) =>
  Math.min(
    distance(word, textWord),
    distance(word, textWord.slice(0, word.length)),
  );

// Whether a text holds the searched word, the typos of its tolerance allowed: a word of the text (or its start, the
// user typing it) is within the tolerated edit distance, whatever the case and accents.
export const approximatelyHolds = (
  text: string,
  searchedWord: string,
  tolerance: SearchTolerance,
) => {
  const word = normalized(searchedWord);
  const tolerated = toleratedTyposOf(word, tolerance);

  return normalized(text)
    .split(WORD_SEPARATOR)
    .filter(Boolean)
    .some((textWord) => {
      const distanceOf = distanceTo(textWord);

      return (
        textWord.includes(word) ||
        distanceOf(word) <= tolerated ||
        (tolerated > 0 &&
          swappedVariantsOf(word).some(
            (swapped) => distanceOf(swapped) <= tolerated - 1,
          ))
      );
    });
};
